import {
  DEFAULT_TEMPLATES,
  DEVICE_ROLES,
  DeviceModel,
  DeviceRole,
  DeviceSetup,
  fillSheet,
  fillTemplate,
  iminPresent,
  kickDrawer,
  knownVendors,
  matchUsb,
  PairedDevice,
  parseScaleFrame,
  pickSerialPort,
  pickUsbDevice,
  printTo,
  readSerialScale,
  resolveDevice,
  resolveParams,
  serialHandle,
  SHEET_LAYOUTS,
  TransportKind,
  usbHandle,
  webSerial,
  webUsb,
} from '@iws/pos-devices'
import { Plus, Printer, Scale, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { useEffect, useMemo, useState } from 'react'

import { Badge, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, PosButton, Switch } from '@/components/ui'
import { useSession } from '@/contexts/SessionContext'
import { downloadPluList } from '@/services/devices/plu'
import { deviceModels, newDeviceId, readSetup, saveSetup, withDevice, withoutDevice } from '@/services/devices/setup'

const ROLE_LABEL: Record<DeviceRole, string> = {
  receipt: 'Receipts and slips',
  label: 'Labels',
  document: 'A4 documents and label sheets',
  scale_read: 'Weighing at the POS',
  scale_labels: 'Scale labels (PLU list)',
  cash_drawer: 'Cash drawer',
  customer_display: 'Customer display',
}

const TRANSPORT_LABEL: Record<TransportKind, string> = {
  browser: 'Browser dialog',
  webusb: 'USB',
  webserial: 'Serial',
  imin: 'iMin',
  http: 'Network',
}

/** A device being added: its handle is known, the cashier picks the model, name and roles. */
interface Draft {
  transport: TransportKind
  handle?: PairedDevice['handle']
  suggested: DeviceModel[]
  model_id: string
  name: string
  roles: DeviceRole[]
  params: Record<string, string | number | boolean>
}

const modelsFor = (transport: TransportKind) => deviceModels().filter((m) => m.transports.includes(transport))

const draftFor = (transport: TransportKind, handle?: PairedDevice['handle'], suggested: DeviceModel[] = [], label?: string): Draft => {
  const model = suggested[0] ?? modelsFor(transport)[0]
  return { transport, handle, suggested, model_id: model?._id ?? '', name: label ?? model?.name ?? 'Device', roles: model?.roles.slice(0, 1) ?? [], params: {} }
}

/** A short sample per role, for "Test". */
const testDocument = (role: DeviceRole | undefined) => {
  const data = {
    shop: { name: 'Test print', address: 'If you can read this, the device works' },
    options: { footer: 'Test', show_price: true },
    order: { number: 'TEST-0001', date: new Date().toLocaleString(), counter: 'Counter 1', cashier: 'Test', lines: [{ name: 'Sample item', qty: '1', price: '10.00', total: '10.00', note: '' }], subtotal: '10.00', total: '10.00', payments: [], items: 1 },
    product: { name: 'Sample product', price: '10.00', unit: 'pc', barcode: 'TEST-0001' },
  }
  if (role === 'label') return fillTemplate(DEFAULT_TEMPLATES.product_label, data, { copies: 1 })
  if (role === 'document') return fillSheet(DEFAULT_TEMPLATES.product_label, Array.from({ length: 6 }, () => data), SHEET_LAYOUTS.labels_3x8)
  return fillTemplate(DEFAULT_TEMPLATES.receipt, data)
}

/**
 * This till's devices (POS.md §18.9): what is paired, which role uses which, and test prints. Several devices at once
 * (receipt printer, label printer, A4 through the browser, a scale). Changes need `devices:setup`; they are saved on
 * the device and backed up to the terminal.
 */
export const DevicesDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const { can, stage } = useSession()
  const editable = stage === 'demo' || can('devices:setup')
  const [setup, setSetup] = useState<DeviceSetup>(readSetup)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [detected, setDetected] = useState<Draft[]>([])
  const [dirty, setDirty] = useState(false)
  const [weight, setWeight] = useState<Record<string, string>>({})

  const models = useMemo(() => (open ? deviceModels() : []), [open])

  useEffect(() => {
    if (!open) return
    setSetup(readSetup())
    setDraft(null)
    setDirty(false)
    // Devices this browser may already use but this till has not paired: offered with their matching models.
    const paired = readSetup().devices
    Promise.all([webUsb()?.getDevices() ?? [], webSerial()?.getPorts() ?? []])
      .then(([usb, serial]) => {
        const found: Draft[] = []
        for (const d of usb) {
          if (paired.some((p) => p.handle?.usb?.vendor_id === d.vendorId && p.handle.usb.product_id === d.productId)) continue
          found.push(draftFor('webusb', { usb: usbHandle(d) }, matchUsb(deviceModels(), d.vendorId, d.productId), d.productName))
        }
        for (const port of serial) {
          const h = serialHandle(port)
          if (paired.some((p) => p.handle?.serial?.usb_vendor_id === h.usb_vendor_id && p.handle.serial.usb_product_id === h.usb_product_id)) continue
          found.push(draftFor('webserial', { serial: h }, matchUsb(deviceModels(), h.usb_vendor_id, h.usb_product_id), 'Serial device'))
        }
        setDetected(found)
      })
      .catch(() => setDetected([]))
  }, [open])

  const update = (next: DeviceSetup) => {
    setSetup(next)
    setDirty(true)
  }

  const run = (work: () => Promise<unknown>) => work().catch((error: Error) => error.name !== 'NotFoundError' && toast.error(error.message))

  const addUsb = () =>
    run(async () => {
      const d = await pickUsbDevice(knownVendors(models))
      setDraft(draftFor('webusb', { usb: usbHandle(d) }, matchUsb(models, d.vendorId, d.productId), d.productName))
    })
  const addSerial = () =>
    run(async () => {
      const port = await pickSerialPort(knownVendors(models))
      const h = serialHandle(port)
      setDraft(draftFor('webserial', { serial: h }, matchUsb(models, h.usb_vendor_id, h.usb_product_id), 'Serial device'))
    })

  const commitDraft = () => {
    if (!draft) return
    const model = models.find((m) => m._id === draft.model_id)
    if (!model) return toast.error('Choose a model')
    if (draft.transport === 'http' && !draft.handle?.http?.url) return toast.error('Enter the printer address')
    const device: PairedDevice = { id: newDeviceId(), model_id: model._id, name: draft.name.trim() || model.name, transport: draft.transport, handle: draft.handle, params: Object.keys(draft.params).length ? draft.params : undefined }
    update(withDevice(setup, device, draft.roles))
    setDetected((list) => list.filter((d) => d !== draft))
    setDraft(null)
  }

  const toggleRole = (role: DeviceRole, id: string, on: boolean) =>
    update({ ...setup, roles: { ...setup.roles, [role]: on ? [...(setup.roles[role] ?? []), id] : (setup.roles[role] ?? []).filter((d) => d !== id) } })

  const test = (device: PairedDevice) =>
    run(async () => {
      const resolved = resolveDevice(device, models)
      if (!resolved) throw new Error('This device’s model is not in the catalog any more')
      if (resolved.model.protocol === 'serial_scale') {
        const frame = resolved.model.scale?.frame
        if (!frame || !device.handle?.serial) throw new Error('This scale has no reading format in the catalog')
        const stop = await readSerialScale(device.handle.serial, frame, (text) => {
          const r = parseScaleFrame(text, frame)
          if (r) setWeight((w) => ({ ...w, [device.id]: `${r.kg.toFixed(3)} kg${r.stable ? '' : ' …'}` }))
        })
        setTimeout(() => stop(), 10_000)
        return
      }
      const role = DEVICE_ROLES.find((r) => setup.roles[r]?.includes(device.id))
      await printTo(resolved, testDocument(role), { title: 'Test print' })
    })

  const openDrawer = (device: PairedDevice) =>
    run(async () => {
      const resolved = resolveDevice(device, models)
      if (!resolved) throw new Error('This device’s model is not in the catalog any more')
      await kickDrawer(resolved)
    })

  const save = async () => {
    const { backedUp } = await saveSetup(setup, stage !== 'demo')
    setDirty(false)
    toast.success(backedUp || stage === 'demo' ? 'Devices saved' : 'Devices saved on this device; the backup to the terminal will be retried next time')
    onOpenChange(false)
  }

  const draftModel = draft ? models.find((m) => m._id === draft.model_id) : undefined
  const draftParams = draftModel ? resolveParams(draftModel.protocol, draftModel.params) : {}

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Devices</DialogTitle>
          <DialogDescription>Printers, scales and the cash drawer on this device. Saved here and on the terminal.</DialogDescription>
        </DialogHeader>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-ink">On this device</h3>
          {setup.devices.length === 0 && <p className="text-sm text-muted-foreground">No devices yet.</p>}
          {setup.devices.map((d) => {
            const model = models.find((m) => m._id === d.model_id)
            return (
              <div key={d.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3">
                {model?.type === 'scale' ? <Scale className="size-4 text-muted-foreground" /> : <Printer className="size-4 text-muted-foreground" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{d.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {model?.name ?? 'Unknown model'} · {TRANSPORT_LABEL[d.transport]}
                    {weight[d.id] && ` · ${weight[d.id]}`}
                  </p>
                </div>
                {model?.scale?.plu_export && (
                  <PosButton
                    variant="outline"
                    onClick={() =>
                      run(async () => {
                        const count = await downloadPluList(model.scale!.plu_export!, d.name)
                        toast.success(`PLU list saved: ${count} products. Load it into the scale with its own software.`)
                      })
                    }
                  >
                    PLU list
                  </PosButton>
                )}
                {(setup.roles.cash_drawer?.includes(d.id) || model?.protocol === 'escpos' || model?.protocol === 'star') && (
                  <PosButton variant="outline" onClick={() => openDrawer(d)}>
                    Open drawer
                  </PosButton>
                )}
                {model?.protocol !== 'none' && (
                  <PosButton variant="outline" onClick={() => test(d)}>
                    {model?.protocol === 'serial_scale' ? 'Read weight' : 'Test'}
                  </PosButton>
                )}
                {editable && (
                  <button type="button" aria-label={`Remove ${d.name}`} className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-fill hover:text-error" onClick={() => update(withoutDevice(setup, d.id))}>
                    <Trash2 className="size-4" />
                  </button>
                )}
              </div>
            )
          })}
        </section>

        {editable && detected.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-ink">Detected</h3>
            {detected.map((d, i) => (
              <div key={i} className="flex items-center gap-2 rounded-md border border-dashed border-border p-3 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {d.name} · {TRANSPORT_LABEL[d.transport]} {d.suggested.length > 0 && <Badge tone="soft">{d.suggested[0].name}</Badge>}
                </span>
                <PosButton variant="secondary" onClick={() => setDraft(d)}>
                  Add
                </PosButton>
              </div>
            ))}
          </section>
        )}

        {editable && !draft && (
          <section className="flex flex-wrap gap-2">
            {webUsb() && (
              <PosButton variant="outline" onClick={addUsb}>
                <Plus /> USB device
              </PosButton>
            )}
            {webSerial() && (
              <PosButton variant="outline" onClick={addSerial}>
                <Plus /> Serial device
              </PosButton>
            )}
            <PosButton variant="outline" onClick={() => setDraft(draftFor('http', { http: { url: '', flavor: 'epos' } }))}>
              <Plus /> Network printer
            </PosButton>
            <PosButton variant="outline" onClick={() => setDraft(draftFor('browser', undefined, [], 'Browser print dialog'))}>
              <Plus /> Printer via browser
            </PosButton>
            <PosButton variant="outline" onClick={() => setDraft(draftFor('browser', undefined, models.filter((m) => m.roles.includes('scale_labels')), 'Label scale'))}>
              <Plus /> Label scale
            </PosButton>
            {iminPresent() && (
              <PosButton variant="outline" onClick={() => setDraft(draftFor('imin', undefined, models.filter((m) => m.transports.includes('imin'))))}>
                <Plus /> iMin printer
              </PosButton>
            )}
          </section>
        )}

        {draft && (
          <section className="space-y-3 rounded-md border border-border bg-subdued p-3">
            <h3 className="text-sm font-semibold text-ink">New device · {TRANSPORT_LABEL[draft.transport]}</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="dev-name">Name</Label>
                <Input id="dev-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="dev-model">Model</Label>
                <select
                  id="dev-model"
                  value={draft.model_id}
                  onChange={(e) => {
                    const m = models.find((x) => x._id === e.target.value)
                    setDraft({ ...draft, model_id: e.target.value, roles: m?.roles.slice(0, 1) ?? [], params: {} })
                  }}
                  className="h-9 rounded-sm border border-input bg-card px-3 text-sm text-ink"
                >
                  {[...draft.suggested, ...modelsFor(draft.transport).filter((m) => !draft.suggested.includes(m))].map((m) => (
                    <option key={m._id} value={m._id}>
                      {m.name}
                      {draft.suggested.includes(m) ? ' (detected)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              {draft.transport === 'http' && (
                <div className="grid gap-1.5 sm:col-span-2">
                  <Label htmlFor="dev-url">Printer address</Label>
                  <Input
                    id="dev-url"
                    placeholder="http://192.168.1.50/cgi-bin/epos/service.cgi?devid=local_printer"
                    value={draft.handle?.http?.url ?? ''}
                    onChange={(e) => setDraft({ ...draft, handle: { http: { url: e.target.value, flavor: draft.handle?.http?.flavor ?? 'epos' } } })}
                  />
                </div>
              )}
            </div>
            {draftModel && (
              <div className="space-y-1.5">
                <Label>Used for</Label>
                <div className="flex flex-wrap gap-3">
                  {draftModel.roles.map((role) => (
                    <label key={role} className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={draft.roles.includes(role)}
                        onChange={(e) => setDraft({ ...draft, roles: e.target.checked ? [...draft.roles, role] : draft.roles.filter((r) => r !== role) })}
                      />
                      {ROLE_LABEL[role]}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {Object.keys(draftParams).length > 0 && (
              <details>
                <summary className="cursor-pointer text-sm text-text-2">Settings for this device (paper, label size…)</summary>
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {Object.entries(draftParams).map(([key, value]) => (
                    <div key={key} className="grid gap-1">
                      <Label htmlFor={`p-${key}`} className="text-xs">
                        {key.replace(/_/g, ' ')}
                      </Label>
                      <Input
                        id={`p-${key}`}
                        defaultValue={String(value)}
                        onChange={(e) => {
                          const raw = e.target.value
                          const typed = typeof value === 'number' ? Number(raw) : typeof value === 'boolean' ? raw === 'true' : raw
                          setDraft({ ...draft, params: { ...draft.params, [key]: typed } })
                        }}
                      />
                    </div>
                  ))}
                </div>
              </details>
            )}
            <div className="flex justify-end gap-2">
              <PosButton variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </PosButton>
              <PosButton onClick={commitDraft}>
                Add device
              </PosButton>
            </div>
          </section>
        )}

        {setup.devices.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-ink">Which device does what</h3>
            <div className="divide-y divide-border-subtle rounded-md border border-border">
              {DEVICE_ROLES.filter((role) => setup.devices.some((d) => models.find((m) => m._id === d.model_id)?.roles.includes(role))).map((role) => (
                <div key={role} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm">
                  <span className="w-48 font-medium text-ink">{ROLE_LABEL[role]}</span>
                  {setup.devices
                    .filter((d) => models.find((m) => m._id === d.model_id)?.roles.includes(role))
                    .map((d) => (
                      <label key={d.id} className="flex items-center gap-1.5">
                        <input type="checkbox" disabled={!editable} checked={setup.roles[role]?.includes(d.id) ?? false} onChange={(e) => toggleRole(role, d.id, e.target.checked)} />
                        {d.name}
                      </label>
                    ))}
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="grid gap-3 sm:grid-cols-2">
          <label className="flex items-center justify-between gap-3 text-sm">
            Print after every sale
            <Switch disabled={!editable} checked={!!setup.options?.auto_print} onCheckedChange={(v) => update({ ...setup, options: { ...setup.options, auto_print: v } })} />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm">
            Open the cash drawer on cash sales
            <Switch disabled={!editable} checked={!!setup.options?.open_drawer_on_cash} onCheckedChange={(v) => update({ ...setup, options: { ...setup.options, open_drawer_on_cash: v } })} />
          </label>
        </section>

        <DialogFooter>
          {!editable && <p className="mr-auto text-xs text-muted-foreground">You can test devices; changing them needs the devices setup permission.</p>}
          <PosButton variant="outline" size="touch" onClick={() => onOpenChange(false)}>
            Close
          </PosButton>
          {editable && (
            <PosButton size="touch" disabled={!dirty} onClick={save}>
              Save
            </PosButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
